import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import {
  CreateFormRequest,
  GetFormListResponse,
  GetFormListV1FormsGetParams,
  GetFormsItemsListResponse,
  GetFormsItemsListV1FormsItemsGetParams,
  UpdateFormRequest,
} from 'compliance-api';
import { FormikErrors, FormikValues } from 'formik';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { CURRENT_FORM_QUERY_KEY } from '@/modules/common/queries/forms.ts';
import {
  CURRENT_USER_TASKS_LIST_QUERY_KEY,
  CURRENT_USER_TASK_QUERY_KEY,
} from '@/modules/common/queries/tasks.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { CUSTOM_CHARTS_FORM_FIELDS_QUERY_KEY } from '@/modules/admin-dashboard/queries/custom-charts.ts';

export const CURRENT_USER_FORMS_LIST_QUERY_KEY = 'vestra-compliance/forms-list';
export const CURRENT_USER_FORMS_ITEMS_LIST_QUERY_KEY = 'vestra-compliance/forms-items-list';

export const useCurrentUserFormsListQuery = (
  payload: GetFormListV1FormsGetParams,
  options?: { disabled?: boolean; cacheKey?: string },
) => {
  const { page } = payload;
  const { disabled = false, cacheKey } = options || {};
  const businessId = useGetCurrentBusinessId();

  return useInfiniteQuery<GetFormListResponse>({
    queryKey: [CURRENT_USER_FORMS_LIST_QUERY_KEY, JSON.stringify(payload), cacheKey, businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getFormListV1FormsGet({
        ...payload,
        page: typeof pageParam === 'number' && pageParam ? pageParam : pageParam?.page || page,
        filter_by_name: pageParam?.search || payload.filter_by_name,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useFormsItemsListQuery = (
  payload: GetFormsItemsListV1FormsItemsGetParams,
  options?: { disabled?: boolean; cacheKey?: string },
) => {
  const { page } = payload;
  const { disabled = false, cacheKey } = options || {};

  return useInfiniteQuery<GetFormsItemsListResponse>({
    queryKey: [
      CURRENT_USER_FORMS_LIST_QUERY_KEY,
      JSON.stringify(payload),
      cacheKey,
      useGetCurrentBusinessId(),
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getFormsItemsListV1FormsItemsGet({
        ...payload,
        page: typeof pageParam === 'number' && pageParam ? pageParam : pageParam?.page || page,
        filter_by_name: pageParam?.search || payload.filter_by_name,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useCreateFormMutation = (setErrors?: (errors: FormikErrors<FormikValues>) => void) => {
  return useMutation({
    mutationFn: async (payload: CreateFormRequest) => {
      const response = await withErrorHandling(api.v1.createFormV1FormsPost(payload), {
        loading: 'Creating form...',
        success: 'Form created',
        setErrors,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_FORMS_LIST_QUERY_KEY]);
    },
  });
};

export const useUpdateFormMutation = (setErrors?: (errors: FormikErrors<FormikValues>) => void) => {
  return useMutation({
    mutationFn: async (payload: UpdateFormRequest) => {
      const response = await withErrorHandling(api.v1.updateFormV1FormsPatch(payload), {
        loading: 'Updating form...',
        success: 'Form updated',
        setErrors,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_FORM_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_FORMS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASK_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_USER_TASKS_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CUSTOM_CHARTS_FORM_FIELDS_QUERY_KEY]);
    },
  });
};

export const useDeleteFormMutation = () => {
  return useMutation({
    mutationFn: async (form_id: number) => {
      const response = await withErrorHandling(api.v1.removeFormV1FormsIdDelete({ id: form_id }), {
        loading: 'Archiving form...',
        success: 'Form archived',
        notificationDuration: 7500,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_FORMS_LIST_QUERY_KEY]);
    },
  });
};

export const useRestoreFormMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(api.v1.restoreFormV1FormsRestorePost({ id }), {
        loading: 'Restoring form...',
        success: 'Form restored',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_FORMS_LIST_QUERY_KEY]);
    },
  });
};
